import React, { useState } from 'react';
import {
  TEMPLATES, buildLines, computeLine, SaisieInput,
  TypologieKey, Template, RatioRef, Saisies,
} from './Ratio_GrosOeuvrePrototype';
import Tooltip from './Ratio_Tooltip';

// ─── Ratio config ────────────────────────────────────────────────────────────
// Un client = cette liste (définitions) + ses benchmarks. Le composant ne change pas.

type Ctx = {
  s: Saisies;
  hProd: number; hInd: number;
  totalMo: number; totalMat: number; totalDbs: number; totalFc: number; total: number;
  mensuelFc: number; encadrement: number; elec: number; prefa: number; acier: number;
};

type Bench = { min: number; p25: number; med: number; p75: number; max: number; nb?: number };
type Format = 'dec' | 'k' | 'pct';

type RatioDef = {
  id: string;
  label: string;
  unit: string;
  format: Format;
  compute?: (c: Ctx) => number;          // absent = benchmark seul (non chiffré dans l'estimation)
  bench: (t: Template, k: TypologieKey) => Bench;
};

// Quantile par interpolation linéaire (même convention qu'Excel CENTILE.INCLURE)
const quantile = (values: number[], q: number) => {
  const v = [...values].sort((a, b) => a - b);
  const i = (v.length - 1) * q;
  const lo = Math.floor(i);
  return v[lo] + (v[Math.min(lo + 1, v.length - 1)] - v[lo]) * (i - lo);
};

const fromRef = (r: RatioRef): Bench => {
  const vals = r.source.projets.map(p => p.valeur);
  return {
    min: r.source.range.min, p25: quantile(vals, 0.25), med: r.value,
    p75: quantile(vals, 0.75), max: r.source.range.max, nb: r.source.nbProjets,
  };
};

// ponytail: benchmarks mock pour les ratios dérivés (P25/P75 interpolés à mi-chemin), à remplacer par les stats projets du client
const mock = (resid: [number, number, number], tert: [number, number, number]) =>
  (_: Template, k: TypologieKey): Bench => {
    const [min, med, max] = k === 'resid-neuf' ? resid : tert;
    return { min, p25: (min + med) / 2, med, p75: (med + max) / 2, max };
  };

const FAMILIES: { label: string; ratios: RatioDef[] }[] = [
  {
    label: 'Structure',
    ratios: [
      { id: 'm3-m2',    label: 'Béton par m² SHOT',      unit: 'm³/m²',  format: 'dec', compute: c => c.s.m3 / c.s.shot,  bench: mock([0.55, 0.70, 0.85], [0.60, 0.75, 0.92]) },
      { id: 'acier-m3', label: 'Acier par m³ de béton',  unit: 'kg/m³',  format: 'dec', compute: c => c.acier, bench: t => fromRef(t.acierRatio) },
      { id: 'prefa',    label: 'Préfabrication par m² SHOT', unit: '€/m²', format: 'dec', compute: c => c.prefa, bench: t => fromRef(t.prefaPu) },
      { id: 'mat-m2',   label: 'Matériaux par m² SHOT',  unit: '€/m²',   format: 'dec', compute: c => c.totalMat / c.s.shot, bench: mock([130, 155, 185], [150, 178, 210]) },
    ],
  },
  {
    label: "Main d'œuvre",
    ratios: [
      { id: 'thm',      label: 'Taux horaire moyen',     unit: '€/h',    format: 'dec', compute: c => c.s.tauxHoraire,      bench: mock([38, 42.85, 48], [40, 45, 51]) },
      { id: 'hprod-m2', label: 'MO production par m² SHOT', unit: 'h/m²', format: 'dec', compute: c => c.hProd / c.s.shot,  bench: mock([4.2, 5.22, 6.3], [3.6, 4.6, 5.6]) },
      { id: 'hind-m2',  label: 'MO indirecte par m² SHOT',  unit: 'h/m²', format: 'dec', compute: c => c.hInd / c.s.shot,   bench: mock([1.7, 2.24, 2.8], [1.4, 1.8, 2.3]) },
      { id: 'hprod-m3', label: 'MO production par m³ de béton',   unit: 'h/m³',   format: 'dec', compute: c => c.hProd / c.s.m3,     bench: t => fromRef(t.moProductionRatio) },
      { id: 'hind-m3',  label: 'MO indirecte par m³ de béton',    unit: 'h/m³',   format: 'dec', compute: c => c.hInd / c.s.m3,      bench: t => fromRef(t.moIndirecteRatio) },
    ],
  },
  {
    label: 'Frais de chantier',
    ratios: [
      { id: 'shot-mois',  label: 'm² SHOT mensuel',        unit: 'm²/mois', format: 'dec', compute: c => c.s.shot / c.s.duree, bench: mock([200, 346, 480], [220, 380, 520]) },
      { id: 'fc-mois',    label: 'Frais de chantier mensuel', unit: '€/mois', format: 'k', compute: c => c.mensuelFc,         bench: mock([40000, 46000, 58000], [46000, 54000, 66000]) },
      { id: 'encadr',     label: "Frais d'encadrement mensuel", unit: '€/mois', format: 'k', compute: c => c.encadrement / c.s.duree, bench: t => fromRef(t.encadrementPu) },
      { id: 'encadr-dbs', label: 'Encadrement sur DBS',    unit: '',        format: 'pct', compute: c => c.encadrement / c.totalDbs, bench: mock([0.22, 0.28, 0.36], [0.20, 0.26, 0.33]) },
      { id: 'elec',       label: 'Coût électrique',        unit: '€',       format: 'dec', compute: c => c.elec,              bench: t => fromRef(t.instaElecPu) },
      { id: 'grues',      label: 'Frais grues mensuel',    unit: '€/mois',  format: 'k',                                       bench: mock([9000, 11390, 14000], [11000, 13500, 16500]) },
    ],
  },
];

// Affiché à côté de la calculette : premier résultat lu
const COUT_GLOBAL: RatioDef =
  { id: 'eur-m2', label: 'Coût moyen par m² SHOT', unit: '€/m²', format: 'dec', compute: c => c.total / c.s.shot, bench: mock([680, 812, 950], [760, 900, 1060]) };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v: number, f: Format) =>
  f === 'pct' ? `${(v * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`
  : f === 'k' ? `${(v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k`
  : v.toLocaleString('fr-FR', { maximumFractionDigits: v >= 100 ? 0 : 2 });

const eur = (v: number) => `${Math.round(v).toLocaleString('fr-FR')} €`;

// Écart hors fourchette, en part de la largeur de fourchette : gris → orange clair → orange foncé
type Status = 'in' | 'near' | 'out';
const statusOf = (v: number, b: Bench): Status => {
  if (v >= b.min && v <= b.max) return 'in';
  const gap = v < b.min ? b.min - v : v - b.max;
  return gap / (b.max - b.min) <= 0.25 ? 'near' : 'out';
};

const STATUS_STYLE: Record<Status, { stroke: string; text: string; label: string }> = {
  in:   { stroke: '#8b5cf6', text: 'text-gray-900',   label: 'Dans la fourchette' },
  near: { stroke: '#fdba74', text: 'text-orange-500', label: 'Légèrement hors fourchette' },
  out:  { stroke: '#ea580c', text: 'text-orange-700', label: 'Hors fourchette' },
};

// ─── Ring ────────────────────────────────────────────────────────────────────

const R = 42;
const C = 2 * Math.PI * R;
const R_BAND = 50;                    // fine bande P25–P75 à l'extérieur de l'anneau
const C_BAND = 2 * Math.PI * R_BAND;

// uncontrolled : la valeur existe mais découle de la médiane → anneau grisé, pas d'alerte
const Ring: React.FC<{ def: RatioDef; value: number | null; bench: Bench; uncontrolled?: string }> = ({ def, value, bench, uncontrolled }) => {
  const muted = value == null || !!uncontrolled;
  const shown = value ?? bench.med;
  const status: Status = muted ? 'in' : statusOf(value!, bench);
  const style = STATUS_STYLE[status];
  const span = bench.max - bench.min;
  const pos = Math.min(1, Math.max(0, (shown - bench.min) / span));
  const medAngle = ((bench.med - bench.min) / span) * 360 - 90;
  const p25Pos = (bench.p25 - bench.min) / span;
  const p75Pos = (bench.p75 - bench.min) / span;
  // Libellé médiane au bout du repère, côté intérieur de l'anneau
  const medRad = ((bench.med - bench.min) / span) * 2 * Math.PI;
  const medLabel = { left: 52 + 32 * Math.sin(medRad), top: 52 - 32 * Math.cos(medRad) };

  const tooltip = [
    `${def.label}${def.unit ? ` (${def.unit})` : ''}`,
    `Médiane : ${fmt(bench.med, def.format)}`,
    `P25–P75 : ${fmt(bench.p25, def.format)} – ${fmt(bench.p75, def.format)}`,
    `Min–max : ${fmt(bench.min, def.format)} – ${fmt(bench.max, def.format)}`,
    bench.nb ? `${bench.nb} projets de référence` : 'Référence indicative',
    uncontrolled ?? (value == null ? 'Non chiffré dans l\'estimation : médiane affichée' : style.label),
  ].join('\n');

  return (
    <div className="flex flex-col items-center gap-1.5 w-[130px]">
      <Tooltip text={tooltip} width="w-64">
      <div className="relative w-[104px] h-[104px]">
        <svg viewBox="0 0 104 104" className="w-full h-full -rotate-90">
          <circle cx="52" cy="52" r={R} fill="none" stroke="#f1f5f9" strokeWidth="9" />
          <circle
            cx="52" cy="52" r={R_BAND} fill="none" stroke="#ddd6fe" strokeWidth="3"
            strokeDasharray={`${(p75Pos - p25Pos) * C_BAND} ${C_BAND}`}
            strokeDashoffset={-p25Pos * C_BAND}
          />
          <circle
            cx="52" cy="52" r={R} fill="none"
            stroke={muted ? '#cbd5e1' : style.stroke}
            strokeWidth="9" strokeLinecap="round"
            strokeDasharray={`${Math.max(pos * C, 0.01)} ${C}`}
            className="transition-all duration-500"
          />
        </svg>
        {/* Repère médiane */}
        <div className="absolute inset-0" style={{ transform: `rotate(${medAngle + 90}deg)` }}>
          <div className="absolute left-1/2 top-[1px] -translate-x-1/2 w-[2px] h-[12px] bg-gray-700 rounded" />
        </div>
        <span
          className="absolute -translate-x-1/2 -translate-y-1/2 text-[8px] leading-none text-gray-500 tabular-nums whitespace-nowrap"
          style={medLabel}
        >
          {fmt(bench.med, def.format)}
        </span>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-lg font-semibold tabular-nums leading-none ${muted ? 'text-gray-400' : style.text}`}>
            {fmt(shown, def.format)}
          </span>
          {def.unit && <span className="text-[10px] text-gray-400 mt-0.5">{def.unit}</span>}
        </div>
      </div>
      </Tooltip>
      <span className="text-[11px] text-gray-700 text-center leading-tight">{def.label}</span>
    </div>
  );
};

// ─── Main ────────────────────────────────────────────────────────────────────

const RatioGrosOeuvreDirection: React.FC = () => {
  const [typologie, setTypologie] = useState<TypologieKey>('resid-neuf');
  const [shot, setShot] = useState(1260);
  const [m3Override, setM3Override] = useState<number | null>(null);
  const [duree, setDuree] = useState(5);
  const [tauxHoraire, setTauxHoraire] = useState(43);

  const template = TEMPLATES[typologie];
  const m3Bench = FAMILIES[0].ratios[0].bench(template, typologie).med;
  const s: Saisies = { shot, m3: m3Override ?? Math.round(shot * m3Bench), duree, tauxHoraire };

  const comps = buildLines(template).map(line => ({ line, comp: computeLine(line, undefined, s) }));
  const byId = (id: string) => comps.find(c => c.line.id === id)!.comp;
  const sum = (cat: string) => comps.filter(c => c.line.category === cat).reduce((a, c) => a + c.comp.total, 0);

  const totalMo = sum('mo');
  const totalMat = sum('materiaux');
  const totalFc = sum('fc');
  const ctx: Ctx = {
    s,
    hProd: byId('mo-prod').qty.value,
    hInd: byId('mo-ind').qty.value,
    totalMo, totalMat, totalDbs: totalMo + totalMat, totalFc, total: totalMo + totalMat + totalFc,
    mensuelFc: comps.filter(c => c.line.driver.kind === 'from-duree').reduce((a, c) => a + c.comp.pu.value, 0),
    encadrement: byId('encadr').total,
    elec: byId('insta-elec').total,
    prefa: byId('prefa').pu.value,
    acier: byId('acier').qty.value / s.m3,
  };

  const kpis = [
    { label: 'Budget GO estimé', value: eur(ctx.total), sub: `${fmt(ctx.total / shot, 'dec')} €/m² SHOT` },
    { label: 'Déboursé sec', value: eur(ctx.totalDbs), sub: `MO ${eur(totalMo)} · Mat. ${eur(totalMat)}` },
    { label: 'Frais de chantier', value: eur(totalFc), sub: `${fmt(ctx.mensuelFc, 'k')} €/mois` },
    { label: "Heures de main d'œuvre", value: `${Math.round(ctx.hProd + ctx.hInd).toLocaleString('fr-FR')} h`, sub: `${fmt(ctx.acier * s.m3 / 1000, 'dec')} t d'acier` },
  ];

  const nbAlerts = [COUT_GLOBAL, ...FAMILIES.flatMap(f => f.ratios)]
    .filter(r => r.compute && statusOf(r.compute(ctx), r.bench(template, typologie)) !== 'in').length;

  return (
    <div className="space-y-4">
      {/* Calculette SHOT */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-4 pb-3 border-b border-gray-100">
          <span className="font-semibold text-gray-900">Calculette rapide · Gros Œuvre</span>
          <div className="flex gap-1">
            {(Object.entries(TEMPLATES) as [TypologieKey, Template][]).map(([key, t]) => (
              <button
                key={key}
                onClick={() => setTypologie(key)}
                className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                  typologie === key
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-violet-400 hover:text-violet-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-6">
        <div className="flex-1 min-w-0">
        <div className="flex items-start gap-8 py-4 border-b border-gray-100">
          <SaisieInput label="SHOT" unit="m²" value={shot} onChange={setShot} decimals={0} />
          <div className="flex items-end gap-1">
            <SaisieInput label={m3Override == null ? 'Béton (estimé)' : 'Béton'} unit="m³" value={s.m3} onChange={setM3Override} decimals={0} estimated={m3Override == null} />
            {m3Override != null && (
              <button onClick={() => setM3Override(null)} className="text-[10px] text-gray-400 hover:text-gray-700 underline mb-1" title="Revenir au volume estimé (SHOT × médiane)">estimer</button>
            )}
          </div>
          <SaisieInput label="Durée" unit="mois" value={duree} onChange={setDuree} decimals={0} />
          <SaisieInput label="Taux horaire" unit="€/h" value={tauxHoraire} onChange={setTauxHoraire} decimals={2} />
        </div>

        <div className="grid grid-cols-4 gap-4 pt-4">
          {kpis.map(k => (
            <div key={k.label}>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em]">{k.label}</div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums mt-0.5">{k.value}</div>
              <div className="text-[11px] text-gray-500 tabular-nums">{k.sub}</div>
            </div>
          ))}
        </div>
        </div>
        <div className="flex items-center border-l border-gray-100 pl-6 pt-4">
          <Ring def={COUT_GLOBAL} value={COUT_GLOBAL.compute!(ctx)} bench={COUT_GLOBAL.bench(template, typologie)} />
        </div>
        </div>
      </div>

      {/* Ratios de contrôle */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-4 pb-3 border-b border-gray-100">
          <span className="font-semibold text-gray-900">Ratios de contrôle</span>
          <div className="flex items-center gap-4 text-[11px] text-gray-500">
            <span>{nbAlerts === 0 ? 'Tous les ratios dans la fourchette' : `${nbAlerts} ratio${nbAlerts > 1 ? 's' : ''} hors fourchette`}</span>
            <span className="text-gray-300">|</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" />Dans la fourchette</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-300" />Légèrement hors</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-600" />Hors fourchette</span>
            <span className="inline-flex items-center gap-1"><span className="w-[2px] h-3 bg-gray-700 rounded" />Médiane</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-[3px] bg-violet-200 rounded" />P25–P75</span>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {FAMILIES.map(f => (
            <div key={f.label} className="py-4">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em] mb-3">{f.label}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-5">
                {f.ratios.map(r => (
                  <Ring
                    key={r.id} def={r} value={r.compute ? r.compute(ctx) : null} bench={r.bench(template, typologie)}
                    uncontrolled={r.id === 'm3-m2' && m3Override == null ? 'Estimé à partir de la médiane, non contrôlé : saisissez le volume de béton' : undefined}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-[10px] text-gray-400 pt-2">
          L'anneau situe la valeur de l'estimation entre le minimum et le maximum des projets de référence · {template.label} · survolez un anneau pour la médiane, le P25–P75 et la fourchette.
        </p>
      </div>
    </div>
  );
};

export default RatioGrosOeuvreDirection;
